import { useEffect, useState } from 'react';
import { useBackend } from '@/services/backend';
import { availableShellEntries } from '@/lib/projectSettings';

export interface AvailableShell {
  id: string;
  name: string;
}

/** The static, platform-appropriate list — used as the initial value and as a
 *  fallback when the backend can't detect installed shells (web/WebRTC). */
function staticShells(): AvailableShell[] {
  return availableShellEntries().map(([id, cfg]) => ({ id, name: cfg.name }));
}

/**
 * Shells that actually exist on the host machine. On Tauri this asks the
 * backend to probe PATH (so a Linux box never offers PowerShell/CMD, and a
 * box without `fish` never lists it). Falls back to the static platform list
 * when detection isn't available or returns nothing.
 */
export function useAvailableShells(): AvailableShell[] {
  const backend = useBackend();
  const [shells, setShells] = useState<AvailableShell[]>(staticShells);

  useEffect(() => {
    let cancelled = false;
    backend.terminal.listAvailableShells?.()
      .then((list) => {
        if (!cancelled && list && list.length > 0) setShells(list);
      })
      .catch(() => {
        /* keep the static fallback */
      });
    return () => { cancelled = true; };
  }, [backend]);

  return shells;
}
